import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { payrollGlobalApi as papi, formatCents } from '../../../api/payrollGlobal.js';

/**
 * The run workbench: review per-employee results and drive the lifecycle
 * (calculate → approve → post → pay), then generate payslips. Carries the
 * phase-mandated `payrollx-*` data-testids.
 */
export default function RunDetailPage() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await papi.getRun(id);
      setRun(detail?.run || null);
      setResults(detail?.results || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load run');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function act(label, fn) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(`${label} done`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || `${label} failed`);
    } finally {
      setBusy(false);
    }
  }

  async function genSlip(resultId) {
    await act('Payslip', async () => {
      const out = await papi.generatePayslip(resultId);
      setResults((prev) => prev.map((r) => (r.id === resultId ? { ...r, payslip_url: out?.result?.payslip_url } : r)));
    });
  }

  const status = run?.status;
  const cur = run?.currency || '';

  return (
    <section data-testid="payroll-global-run-detail-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Run — {run ? `${String(run.period_start).slice(0, 10)} → ${String(run.period_end).slice(0, 10)}` : '…'}</h1>
          <p className="muted">
            <Link to="/payroll-global/runs" data-testid="payroll-global-run-back">← All runs</Link>
            {run && <> · Pay group: {run.pay_group_name} · Jurisdiction: {run.jurisdiction_code}</>}
          </p>
        </div>
        <div className="badge" data-testid="payroll-global-run-status">{status || '—'}</div>
      </header>

      {error && <div className="alert error" data-testid="payroll-global-run-error">{error}</div>}
      {notice && <div className="alert info" data-testid="payroll-global-run-notice">{notice}</div>}

      <div className="card actions" data-testid="payroll-global-run-actions">
        <button className="btn" data-testid="payrollx-run-calculate" disabled={busy || status === 'posted' || status === 'paid'} onClick={() => act('Calculate', () => papi.calculateRun(id))}>Calculate</button>
        <button className="btn" data-testid="payrollx-run-approve" disabled={busy || status !== 'calculated'} onClick={() => act('Approve', () => papi.approveRun(id))}>Approve</button>
        <button className="btn primary" data-testid="payrollx-run-post" disabled={busy || status !== 'approved'} onClick={() => act('Post', () => papi.postRun(id))}>Post to GL</button>
        <button className="btn" data-testid="payrollx-run-pay" disabled={busy || status !== 'posted'} onClick={() => act('Pay', () => papi.payRun(id))}>Mark paid</button>
      </div>

      {run?.totals && Object.keys(run.totals).length > 0 && (
        <div className="card" data-testid="payroll-global-run-totals">
          <h3>Totals</h3>
          <p>Gross: <strong>{formatCents(run.totals.grossCents, cur)}</strong> · Net: <strong data-testid="payroll-global-run-net-total">{formatCents(run.totals.netCents, cur)}</strong> · Employer cost: {formatCents(run.totals.employerCostCents, cur)} · Employees: {run.totals.employeeCount}</p>
          {run.journal_entry_id && <p className="muted" data-testid="payroll-global-run-gl">Posted GL entry: {run.journal_entry_id}</p>}
        </div>
      )}

      <div className="card">
        <h3>Results</h3>
        <table className="data-table" data-testid="payroll-global-result-list">
          <thead><tr><th>Employee</th><th>Gross</th><th>Taxable</th><th>Net</th><th>Employer cost</th><th>Payslip</th></tr></thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.id} data-testid={`payrollx-result-row-${r.employee_id}`}>
                <td>{r.employee_name || r.employee_code || r.employee_id}</td>
                <td>{formatCents(r.gross_cents, cur)}</td>
                <td>{formatCents(r.taxable_gross_cents, cur)}</td>
                <td data-testid={`payrollx-result-net-${r.employee_id}`}>{formatCents(r.net_cents, cur)}</td>
                <td>{formatCents(r.employer_cost_cents, cur)}</td>
                <td>
                  <button className="btn ghost" data-testid={`payrollx-payslip-${r.id}`} disabled={busy} onClick={() => genSlip(r.id)}>Generate</button>
                  {r.payslip_url && <a className="btn ghost" data-testid={`payrollx-payslip-link-${r.id}`} href={r.payslip_url} target="_blank" rel="noreferrer">View PDF</a>}
                </td>
              </tr>
            ))}
            {!loading && results.length === 0 && <tr><td colSpan={6} className="muted">No results yet — calculate the run.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
